import { type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleAlert, CircleCheck, XIcon } from 'lucide-react';
import { useBillingScheinBanner } from 'hooks/useBillingScheinBanner';
import { type BillingScheinStatus, ScheinStatusValue, ScheinType } from 'types/billingScheinStatus';
import {
  BannerBox,
  BannerContent,
  BannerDescription,
  BannerIconSlot,
  BannerText,
  BannerTitle,
  type BillingScheinBannerTone,
  DismissButton,
  RecheckButton,
} from './BillingScheinBanner.styles';

interface BillingScheinBannerProps {
  scheinStatus?: BillingScheinStatus | null;
  isRechecking?: boolean;
  onRecheck: () => void;
}

interface BannerLayoutProps {
  tone: BillingScheinBannerTone;
  role: 'alert' | 'status';
  icon: ReactNode;
  title: string;
  description: string;
  action?: ReactNode;
  onDismiss: () => void;
}

function BannerLayout({
  tone,
  role,
  icon,
  title,
  description,
  action,
  onDismiss,
}: BannerLayoutProps) {
  const { t } = useTranslation();
  const titleId = useId();

  return (
    <BannerBox $tone={tone} role={role} aria-labelledby={titleId}>
      <BannerContent>
        <BannerIconSlot>{icon}</BannerIconSlot>
        <BannerText>
          <BannerTitle id={titleId}>{title}</BannerTitle>
          <BannerDescription>{description}</BannerDescription>
        </BannerText>
      </BannerContent>
      {action}
      <DismissButton onClick={onDismiss} aria-label={t('general.dismiss')}>
        <XIcon aria-hidden />
      </DismissButton>
    </BannerBox>
  );
}

export function BillingScheinBanner({
  scheinStatus,
  isRechecking = false,
  onRecheck,
}: BillingScheinBannerProps) {
  const { t } = useTranslation();
  const { view, dismiss } = useBillingScheinBanner(scheinStatus?.status);
  const schein = (type: ScheinType) => t(`billingSchein.banner.scheinType.${type}`);

  if (view === 'resolved') {
    return (
      <BannerLayout
        tone="success"
        role="status"
        icon={<CircleCheck />}
        title={t('billingSchein.banner.resolvedTitle')}
        description={t('billingSchein.banner.resolvedDescription')}
        onDismiss={dismiss}
      />
    );
  }
  if (view !== 'problem' || !scheinStatus) return null;

  const isMissing = scheinStatus.status === ScheinStatusValue.NoSchein;
  const kind = isMissing ? 'noSchein' : 'wrongType';
  return (
    <BannerLayout
      tone="warning"
      role={isMissing ? 'alert' : 'status'}
      icon={<CircleAlert />}
      title={t(`billingSchein.banner.${kind}Title`)}
      description={t(`billingSchein.banner.${kind}Description`, {
        expected: schein(scheinStatus.expectedType),
        existing: scheinStatus.existingTypes.map(schein).join(', '),
      })}
      action={
        <RecheckButton onClick={onRecheck} disabled={isRechecking}>
          {t('billingSchein.banner.recheck')}
        </RecheckButton>
      }
      onDismiss={dismiss}
    />
  );
}
